import React from 'react'
import { ColDef, GridOptions } from 'ag-grid-community'
import { isNil } from 'lodash'
import { CrudEventEnum, OrganisationMetaDetail } from 'api'
import {
  TypedValueGetterParams,
  gridOptionsBase,
  TypedCellRendererParams,
  groupingRowColDef,
  StandardGridColumnId,
  withAllContext,
  createGroupedActionRenderer,
  createActionsRenderer,
  TypedContext
} from 'view/components/datagrid'
import { OnOffButtons } from 'view/components/buttons'
import { TradeSplitActionsButton } from './TradeSplitActionsButton'
import { Locale, Translation } from 'locale'

export enum TradeSplitColumnId {
  MY_FIRM = 'MY_FIRM',
  MY_ENTITY = 'MY_ENTITY',
  COUNTERPARTY_FIRM = 'COUNTERPARTY_FIRM',
  COUNTERPARTY = 'COUNTERPARTY',
  PRODUCT = 'PRODUCT',
  SECTOR = 'SECTOR',
  TRADE_SPLIT_AMOUNT = 'TRADE_SPLIT_AMOUNT',
  TRADE_SPLIT = 'TRADE_SPLIT'
}

const tradeSplitRenderer = React.forwardRef(
  (
    params: TypedCellRendererParams<OrganisationMetaDetail, any, TypedContext<OrganisationMetaDetail> & { disabled: boolean }>,
    ref: React.Ref<any>
  ) => {
    const { context, node } = params

    if (node.group) {
      return null
    }

    return (
      <OnOffButtons
        ref={ref}
        on={node.data.organisationMeta.splitTrades}
        onLabel={Translation.UC_ON}
        offLabel={Translation.UC_OFF}
        onChange={on => !isNil(node.id) && context.updateCellValue(node.id, node.data, 'splitTrades', on)}
        disabled={params.context.disabled || !node.data.availableEvents.includes(CrudEventEnum.UPSERT)}
      />
    )
  }
)

export const TradeSplitColumnDefs: ColDef[] = [
  {
    colId: StandardGridColumnId.CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    lockVisible: true,
    enableRowGroup: false,
    filter: false,
    resizable: false,
    sortable: false
  },
  groupingRowColDef,
  {
    colId: StandardGridColumnId.ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 150,
    minWidth: 150,
    cellClass: 'action-cell',
    headerComponent: withAllContext(createGroupedActionRenderer(TradeSplitActionsButton)),
    cellRenderer: withAllContext(createActionsRenderer(TradeSplitActionsButton)),
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    lockVisible: true,
    enableRowGroup: false,
    filter: false,
    sortable: false
  },
  {
    colId: TradeSplitColumnId.MY_FIRM,
    headerName: Locale.translate(Translation.TC_MY_FIRM),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) => params.data?.ownerOrganisation?.legalName,
    width: 140,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradeSplitColumnId.MY_ENTITY,
    headerName: Locale.translate(Translation.TC_MY_ENTITY),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.ownerTradingEntity?.publishedName ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 140,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradeSplitColumnId.COUNTERPARTY_FIRM,
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.subjectOrganisation?.legalName ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 140,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradeSplitColumnId.COUNTERPARTY,
    headerName: Locale.translate(Translation.TC_COUNTERPARTY),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.subjectTradingEntity?.publishedName ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 140,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradeSplitColumnId.PRODUCT,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.product?.name ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradeSplitColumnId.SECTOR,
    headerName: Locale.translate(Translation.TC_SECTOR),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.productSector?.name ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradeSplitColumnId.TRADE_SPLIT_AMOUNT,
    headerName: Locale.translate(Translation.TC_SPLIT_TRADE_AMOUNT),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) => params.data?.organisationMeta.splitTradeAmount,
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: TradeSplitColumnId.TRADE_SPLIT,
    headerName: Locale.translate(Translation.TC_SPLIT_TRADES),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) => params.data?.organisationMeta.splitTrades,
    cellRenderer: tradeSplitRenderer,
    width: 120,
    filter: 'agSetColumnFilter'
  }
]

export const tradeSplitGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.organisationMeta.id
}
